/**
 * Generic map interfaces and types
 * This allows swapping between different map providers (Leaflet, Google Maps, Mapbox, etc.)
 */

export interface MapPlace {
  id: string;
  name: string;
  address: string;
  description: string;
  sportType: string;
  placeType: string;
  lat: number;
  lng: number;
  rating?: number;
  isOpen?: boolean;
  isFree?: boolean;
  isAccessible?: boolean;
}

export interface CustomMarker {
  id: string;
  lat: number;
  lng: number;
  name: string;
}

/**
 * Props that pages pass to GeographicalMap component
 * This is the public API - everything else is handled internally
 */
export interface MapProps {
  // Required: data to display
  places: MapPlace[];

  // Optional: callback when user clicks on a place
  onPlaceClick?: (place: MapPlace) => void;

  // Optional: build a detail-page URL for a place. When set, the popup
  // shows a "View details" link that navigates there.
  getPlaceDetailUrl?: (place: MapPlace) => string;

  // Optional: override default configuration
  center?: [number, number];
  zoom?: number;

  // Optional: callback to receive map state updates for UI display
  onMapStateChange?: (state: MapState) => void;
}

/**
 * Map state that can be displayed in page UI
 */
export interface MapState {
  userLocation: [number, number] | null;
  locationAddress: string;
  searchRadius: number;
  onRadiusChange: (radius: number) => void;
  geolocationAllowed: boolean;
}

/**
 * Internal props passed to map provider implementations
 * This includes all the internal state managed by GeographicalMap
 */
export interface MapProviderProps extends MapProps {
  center: [number, number];
  zoom: number;
  userLocation: [number, number] | null;
  searchRadius: number;
  onRadiusChange: (radius: number) => void;
  locationAddress?: string;
  onMapClick: (lat: number, lng: number) => void;
  onMapRightClick: (lat: number, lng: number) => void;
  customMarkers: CustomMarker[];
  showRadiusCircle: boolean;
}

/**
 * Interface that all map providers must implement
 */
export interface IMapProvider {
  /**
   * Render the map component
   */
  renderMap(props: MapProviderProps): JSX.Element;
}

/**
 * Available map provider types
 */
export enum MapProviderType {
  LEAFLET = 'leaflet',
  GOOGLE_MAPS = 'google-maps',
  MAPBOX = 'mapbox',
}